import { useEffect, useState, useMemo, useRef, useCallback } from "react";
import handleApi from "services/brm/api-util";
import { userRoleState, projectIdState } from "atoms/atoms-admin";
import { selectedInstanceState } from "atoms/atoms-content";
import { useRecoilValue, useRecoilState } from "recoil";
import { userRoles } from "services/brm/admin-service";
import CustomTable from "components/elements/CustomTableDivStyled";
import ButtonCell from "components/EditComponents/ButtonCell";
import { BrmMainTable } from "brm/tables/BrmTables";
import { accessPointApi } from "services/brm/access-service";
import PropTypes from "prop-types";
import useRefreshTableList from "hooks/useRefreshTableList";
import { TYPE, COMMON } from "constants/brm";
import { LoadingSpinner as Loading } from "components/elements";
import {
  createColumnName,
  createColumnCategoryMapped,
  createColumnMappedNoEdit,
  createColumnActions,
  createColumnBooleanNoEditMapped,
} from "brm/tables/services/column/columnFactory";

import * as ACQueries from "brm/system-model/access/access-queries";

const AccessPointTable = ({ setSelectedRows, refresh, setRefresh }) => {
  const userRole = useRecoilValue(userRoleState);
  const [editMode, setEditMode] = useState(false);
  const updatedName = useRef("");
  const updatedCategory = useRef("");
  const [selectedInstance, setSelectedInstance] = useRecoilState(selectedInstanceState);

  const sysEngineerRole = userRoles.properties[userRoles.SYSTEM_ENGINEER].key;
  const riskAnalystRole = userRoles.properties[userRoles.RISK_ANALYST].key;
  const projectId = useRecoilValue(projectIdState);
  const { data: accessPoints, isError, refetch: refetchAccessPoints } = ACQueries.useAccessPoints(projectId);
  const { data: accessPointCategories } = ACQueries.useAccessPointCategories(projectId);

  const createButton = useCallback(
    (cellProps) => {
      if (userRole === sysEngineerRole || userRole === riskAnalystRole) {
        return (
          <ButtonCell
            selectedRowId={selectedInstance.id}
            elementId={cellProps.cell.row.original.id}
            handleConfirmEditClick={async () => {
              setEditMode(false);

              if (updatedName.current !== "") {
                await handleApi(
                  accessPointApi.setAccessPointNameWithHttpInfo(selectedInstance.id, {
                    body: updatedName.current,
                  })
                );
                setSelectedInstance({
                  id: selectedInstance.id,
                  name: updatedName.current,
                  type: selectedInstance.type,
                });
                updatedName.current = "";
              }

              if (updatedCategory.current !== "") {
                await handleApi(
                  accessPointApi.setAccessPointCategoryWithHttpInfo(selectedInstance.id, {
                    body: updatedCategory.current,
                  })
                );
                updatedCategory.current = "";
              }

              setRefresh(true);
            }}
            setEditMode={setEditMode}
            editMode={editMode}
          />
        );
      }
      return COMMON.na;
    },
    [
      sysEngineerRole,
      riskAnalystRole,
      editMode,
      setRefresh,
      selectedInstance.id,
      userRole,
      selectedInstance.type,
      setSelectedInstance,
    ]
  );

  const columns = useMemo(
    () => [
      createColumnName(selectedInstance.id, setSelectedInstance, editMode, updatedName, "accessPoint"),
      createColumnCategoryMapped("category", selectedInstance.id, accessPointCategories, editMode, updatedCategory),
      createColumnBooleanNoEditMapped("isExternal"),
      createColumnMappedNoEdit("node"),
      createColumnMappedNoEdit("exchange"),
      createColumnActions(createButton, { disableFilters: true }),
    ],
    [editMode, setSelectedInstance, selectedInstance.id, accessPointCategories, createButton]
  );

  // Refresh the table list if project selection changes
  useRefreshTableList(projectId, setRefresh);

  useEffect(() => {
    if (refresh) {
      setRefresh(false);
      refetchAccessPoints();
    }
  }, [refresh, setRefresh, refetchAccessPoints]);

  if (isError) {
    return <div>Error while loading Access Point data</div>;
  }

  if (accessPoints) {
    return (
      <>
        <CustomTable>
          <BrmMainTable
            data={accessPoints}
            columns={columns}
            setSelectedRows={setSelectedRows}
            customProps={{ id: "AccessPointTable_table" }}
            elementName={TYPE.accessPoint}
            showRowSelect={false}
          />
        </CustomTable>
      </>
    );
  }
  return <Loading />;
};

AccessPointTable.propTypes = {
  setSelectedRows: PropTypes.func.isRequired,
  refresh: PropTypes.bool.isRequired,
  setRefresh: PropTypes.func.isRequired,
};

export default AccessPointTable;
